import React, { createContext, useContext, useEffect, useState } from "react";
import { Organization, OrgTheme, User } from "../../Interface/Index";
import organizationService from "../../Services/organization.service";
import AuthService from "../../Services/auth.service";
import { theme as antdTheme } from "antd";
import { CHART_COLORS } from "../../Utils/chartColors";

interface AppContextType {
  organization: Organization | null;
  theme: OrgTheme;
  isBasePlanExpired: boolean;
  isDarkMode: boolean;
  toggleTheme: () => void;
  customTheme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  };
  setCustomTheme: (theme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  }) => void;
  customisedTheme: any;
  currentUser: User | null;
  appInitialized: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [theme, setTheme] = useState<OrgTheme>({
    themeName: "default",
    secondaryColor: "#ffe4ba",
    primaryColor: "#F7A400",
    tertiaryColor: "#F8FFFF",
  });
  const [isBasePlanExpired, setIsBasePlanExpired] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [appInitialized, setAppInitialized] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("isDarkMode");
    return savedTheme === "true";
  });
  const [customTheme, setCustomThemeState] = useState({
    themeName: "",
    primaryColor: "",
    secondaryColor: "",
  });

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => {
      const newMode = !prevMode;
      localStorage.setItem("isDarkMode", newMode.toString());
      return newMode;
    });
  };
  const setCustomTheme = (theme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  }) => {
    setCustomThemeState(theme);
  };
  const { defaultAlgorithm, darkAlgorithm } = antdTheme;

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const [organization, isBasePlanExpired] = await Promise.all([
          organizationService.getOrganizationData(),
          organizationService.checkBasePlanExpiry(),
        ]);

        if (organization) {
          setOrganization(organization);
          setTheme(organization.theme);
          if (organization.theme) {
            setCustomTheme(organization.theme);
          }
        }
        setIsBasePlanExpired(isBasePlanExpired);

        await AuthService.getInstance().checkTokenAndRestoreCurrentUser();
        const user = AuthService.getInstance().getCurrentUser();
        setCurrentUser(user);

        setAppInitialized(true);
      } catch (error) {
        console.error("Error initializing app context:", error);
        setAppInitialized(true);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    if (organization) {
      const { theme } = organization;

      if (theme?.primaryColor) {
        document.documentElement.style.setProperty(
          "--ant-color-primary",
          theme.primaryColor
        );
      }

      if (theme?.secondaryColor) {
        document.documentElement.style.setProperty(
          "--ant-color-secondary",
          theme.secondaryColor
        );
      }
    }
  }, [organization]);

  const customisedTheme = {
    algorithm: [isDarkMode ? darkAlgorithm : defaultAlgorithm],
    token: {
      colorPrimary: customTheme
        ? customTheme.primaryColor
        : antdTheme.useToken().token.colorPrimary,
      colorPrimaryBg: customTheme
        ? customTheme.secondaryColor
        : antdTheme.useToken().token.colorPrimaryBg,
      colorSuccess: CHART_COLORS.success,
      colorWarning: CHART_COLORS.warning,
      colorError: CHART_COLORS.error,
      colorInfo: CHART_COLORS.info,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      fontFamilyCode: "'Courier New', monospace",
    },
    components: {
      Tabs: {
        horizontalItemPadding: "0px 40px",
      },
      Typography: {
        titleMarginTop: 0,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
      Button: {
        primaryShadow: "none",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        // colorPrimary is inherited from token level, which uses organization theme
        colorSuccess: CHART_COLORS.success,
        colorWarning: CHART_COLORS.warning,
        colorError: CHART_COLORS.error,
      },
      Alert: {
        colorSuccess: CHART_COLORS.success,
        colorWarning: CHART_COLORS.warning,
        colorError: CHART_COLORS.error,
        colorInfo: CHART_COLORS.info,
      },
      Tag: {
        colorSuccess: CHART_COLORS.success,
        colorWarning: CHART_COLORS.warning,
        colorError: CHART_COLORS.error,
        colorInfo: CHART_COLORS.info,
      },
      Badge: {
        colorSuccess: CHART_COLORS.success,
        colorWarning: CHART_COLORS.warning,
        colorError: CHART_COLORS.error,
        colorInfo: CHART_COLORS.info,
      },
    },
  };

  return (
    <AppContext.Provider
      value={{
        organization,
        theme,
        isBasePlanExpired,
        isDarkMode,
        toggleTheme,
        customTheme,
        setCustomTheme,
        customisedTheme,
        currentUser,
        appInitialized,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used inside AppProvider");
  }
  return context;
};
